/**
 * 设备检测 Composable
 * 用于检测设备类型、屏幕方向,并监听变化
 */

import { getDeviceType, getOrientation, getDeviceInfo } from '@/utils/device'
import type { DeviceType, Orientation, DeviceInfo } from '@/utils/device'

export function useDeviceDetect() {
  // 当前设备类型
  const deviceType = ref<DeviceType>(getDeviceType())

  // 当前屏幕方向
  const orientation = ref<Orientation>(getOrientation())

  // 完整设备信息
  const deviceInfo = ref<DeviceInfo>(getDeviceInfo())

  /**
   * 更新设备信息
   */
  const updateDeviceInfo = () => {
    deviceType.value = getDeviceType()
    orientation.value = getOrientation()
    deviceInfo.value = getDeviceInfo()
  }

  /**
   * 处理 resize 事件 (防抖)
   */
  let resizeTimer: number | null = null
  const handleResize = () => {
    if (resizeTimer) {
      clearTimeout(resizeTimer)
    }
    resizeTimer = window.setTimeout(() => {
      updateDeviceInfo()
    }, 100) // 100ms 防抖
  }

  /**
   * 处理 orientationchange 事件
   */
  const handleOrientationChange = () => {
    // 延迟更新,等待浏览器完成方向切换
    setTimeout(() => {
      updateDeviceInfo()
    }, 200)
  }

  /**
   * 处理 visibilitychange 事件
   * 当页面从隐藏变为可见时 (如手机锁屏后解锁),重新检测设备方向
   * 解决: 横屏锁屏后竖屏解锁,页面仍显示横屏布局的问题
   */
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      // 延迟更新,等待浏览器完成渲染
      setTimeout(() => {
        updateDeviceInfo()
      }, 100)
    }
  }

  // 计算属性 - 判断设备类型
  const isPCMode = computed(() => deviceType.value === 'pc')
  const isIPadMode = computed(() => deviceType.value === 'ipad')
  const isMobileLandscape = computed(() => deviceType.value === 'mobile-landscape')
  const isMobilePortrait = computed(() => deviceType.value === 'mobile-portrait')
  const isMobileMode = computed(() => isMobileLandscape.value || isMobilePortrait.value)

  // 计算属性 - 判断屏幕方向
  const isLandscape = computed(() => orientation.value === 'landscape')
  const isPortrait = computed(() => orientation.value === 'portrait')

  // 挂载时监听事件
  onMounted(() => {
    updateDeviceInfo()
    window.addEventListener('resize', handleResize)
    window.addEventListener('orientationchange', handleOrientationChange)
    document.addEventListener('visibilitychange', handleVisibilityChange)
  })

  // 卸载时移除监听
  onUnmounted(() => {
    if (resizeTimer) {
      clearTimeout(resizeTimer)
    }
    window.removeEventListener('resize', handleResize)
    window.removeEventListener('orientationchange', handleOrientationChange)
    document.removeEventListener('visibilitychange', handleVisibilityChange)
  })

  return {
    // 设备类型
    deviceType,
    orientation,
    deviceInfo,

    // 判断函数 (计算属性)
    isPCMode,
    isIPadMode,
    isMobileLandscape,
    isMobilePortrait,
    isMobileMode,
    isLandscape,
    isPortrait,

    // 工具函数
    updateDeviceInfo,
  }
}
